import { inject, Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root',
})
export class SasDynamicFormService {
    private translate = inject(TranslateService);

    private fieldMapping: any = Object.freeze({
        'sasInput': this.mapInput.bind(this),
        'sasInputMail': this.mapInputEmail.bind(this),
        'sasSelect': this.mapSelect.bind(this),
        'sasRadiobutton': this.mapRadioButton.bind(this),
        'sasCheckbox': this.mapCheckbox.bind(this),
        'sasTextarea': this.mapTextarea.bind(this),
        'sasMultiselect': this.mapMultiselect.bind(this),
        'multiselect': this.mapSelectMultiple.bind(this),
        'sasGazetteerStreet': this.mapGazetteerStreet.bind(this),
        'sasDateRangePicker': this.mapDatepickerRange.bind(this),
        'sasDatepicker': this.mapDatepicker.bind(this),
        //'sasStreet'
        'sasSwitch': this.mapSwitch.bind(this),
        //'sasSubForm': this.mapSubForm.bind(this),
        'selectSection': this.mapSubForm.bind(this),
        'repeatSection': this.mapRepeatSection.bind(this),
        'sasStreet': this.mapStreet.bind(this),
        'sasGazetteerUprn': this.mapGazetteerUprn.bind(this),
        'sasTimepicker': this.mapTimepicker.bind(this),
        'sasHtml': this.mapHtml.bind(this)
    });

    mapFieldSchema(fieldSchema: any) {
        if (!fieldSchema.hasOwnProperty('fieldGroup')) {
            return this.mapField(fieldSchema, fieldSchema);
        }

        return fieldSchema.fieldGroup.map((field: any) => this.mapField(field, fieldSchema));
    }

    getFormElement(fields: any, elementName: string) {
        let fieldsSet = structuredClone(fields);
        let formElement = fieldsSet.find((element: any) => element.name === elementName);

        if (!formElement) {
            fields.forEach((field: any) => {
                if (!formElement && field.fieldGroup) {
                    fieldsSet = structuredClone(field.fieldGroup);
                    formElement = fieldsSet.find((element: any) => element.name === elementName);
                }
            });
        }

        return formElement ? formElement : null;
    }

    private mapField(field: any, fieldSchema: any) {
        if (!field.type) {
            field.type = 'sasHtml';
        }

        const mapFunction = this.fieldMapping[field.type];

        return mapFunction ? mapFunction(field, fieldSchema) : field;
    }

    //#region Field mapping

    private mapInput(field: any, fieldSchema: any) {
        const pattern = this.sanitizePattern(field.templateOptions.validation?.validation.pattern) || '';

        const mappedField: any = {
            key: field.key,
            type: 'sasInput',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) || this.cleanTranslate(field.templateOptions.placeholder),
                maxLength: field.templateOptions.maxLength,
                readonly: field.templateOptions.sasReadonly,
                required: field.templateOptions.validation?.validation.required ?? false,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || '',
                    pattern: pattern,
                    patternText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.patternText']) || this.cleanTranslate(field.templateOptions.validation?.validation.patternText) || ''
                },
            },
            // The native [attr.pattern] on the rendered <input> only affects the browser's own
            // constraint validation — it never touches formControl.errors. Add a real validator
            // so formControl.errors['pattern'] actually populates and the invalid state/message show.
            validators: pattern ? {
                pattern: {
                    expression: (control: AbstractControl) => !control.value || new RegExp(pattern).test(control.value)
                }
            } : undefined,
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'] ?? field.templateOptions.sasReadonly,
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        }

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapInputEmail(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasInputMail',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) || this.cleanTranslate(field.templateOptions.placeholder),
                maxLength: field.templateOptions.maxLength,
                readonly: field.templateOptions.sasReadonly,
                required: field.templateOptions.validation?.validation.required ?? false,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || '',
                    patternText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.patternText']) || this.cleanTranslate(field.templateOptions.validation?.validation.patternText) || ''
                },
            },
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'] ?? field.templateOptions.sasReadonly,
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapSelect(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasSelect',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) || this.cleanTranslate(field.templateOptions.placeholder),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                valueProp: field.templateOptions.valueProp,
                labelProp: field.templateOptions.labelProp,
                defaultText: this.cleanTranslate(field.expressionProperties?.['templateOptions.defaultText']) || this.cleanTranslate(field.templateOptions.defaultText),
                required: field.templateOptions.validation?.validation.required ?? false,
                dataSource: field.templateOptions.dataSource,
                sasOptions: typeof field.templateOptions.options === 'string' ? JSON.parse(field.templateOptions.options) : field.templateOptions.options,
                readonly: !!field.expressionProperties?.['templateOptions.sasReadonly'],
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || ''
                },
            },
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'],
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapRadioButton(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasRadiobutton',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) || this.cleanTranslate(field.templateOptions.placeholder),
                labels: field.templateOptions.labels,
                values: field.templateOptions.values,
                orientation: field.templateOptions.orientation,
                readonly: field.templateOptions.sasReadonly,
                required: field.templateOptions.validation?.validation.required ?? false,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || ''
                },
            },
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'] ?? field.templateOptions.sasReadonly,
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapCheckbox(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasCheckbox',
            id: field.id,
            name: field.name,
            defaultValue: field.defaultValue,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                required: field.templateOptions.validation?.validation.required ?? false,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || ''
                },
            },
            expressions: {
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapTextarea(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasTextarea',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) || this.cleanTranslate(field.templateOptions.placeholder),
                maxLength: field.templateOptions.maxLength,
                required: field.templateOptions.validation?.validation.required ?? false,
                readonly: field.templateOptions.sasReadonly,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || '',
                    pattern: this.sanitizePattern(field.templateOptions.validation?.validation.pattern) || '',
                    patternText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.patternText']) || this.cleanTranslate(field.templateOptions.validation?.validation.patternText) || ''
                },
            },
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'] ?? field.templateOptions.sasReadonly,
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapMultiselect(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasMultiselect',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                required: field.templateOptions.validation?.validation.required ?? false,
                dataSource: field.templateOptions.dataSource,
                sasOptions: field.templateOptions.options,
                sasOptionsSorted: field.templateOptions.optionsSorted,
                sasFullModel: field.templateOptions.fullmodel,
                sasRemoveActionBar: field.templateOptions.sasRemoveActionBar,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || '',
                },
            },
            expressions: {
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapSelectMultiple(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'multiselect',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) || this.cleanTranslate(field.templateOptions.placeholder),
                multiple: true,
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                valueProp: field.templateOptions.valueProp,
                labelProp: field.templateOptions.labelProp,
                defaultText: this.cleanTranslate(field.expressionProperties?.['templateOptions.defaultText']) || this.cleanTranslate(field.templateOptions.defaultText),
                required: field.templateOptions.validation?.validation.required ?? false,
                dataSource: field.templateOptions.dataSource,
                sasOptions: field.templateOptions.options,
                readonly: !!field.expressionProperties?.['templateOptions.sasReadonly'],
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || ''
                },
            },
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'],
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapGazetteerStreet(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasGazetteerStreet',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) || this.cleanTranslate(field.templateOptions.placeholder),
                maxLength: field.templateOptions.maxLength,
                readonly: field.templateOptions.sasReadonly,
                required: field.templateOptions.validation?.validation.required ?? false,
                maxPostcodes: field.templateOptions.maxPostcodes,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || '',
                    pattern: this.sanitizePattern(field.templateOptions.validation?.validation.pattern) || '',
                    patternText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.patternText']) || this.cleanTranslate(field.templateOptions.validation?.validation.patternText) || ''
                },
            },
            expressions: {
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapDatepickerRange(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasDateRangePicker',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) || this.cleanTranslate(field.templateOptions.placeholder),
                maxLength: field.templateOptions.maxLength,
                readonly: field.templateOptions.sasReadonly,
                required: field.templateOptions.validation?.validation.required ?? false,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || '',
                    pattern: this.sanitizePattern(field.templateOptions.validation?.validation.pattern) || '',
                    patternText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.patternText']) || this.cleanTranslate(field.templateOptions.validation?.validation.patternText) || ''
                },
            },
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'] ?? field.templateOptions.sasReadonly,
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapDatepicker(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasDatepicker',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) && this.cleanTranslate(field.templateOptions.placeholder),
                maxLength: field.templateOptions.maxLength,
                readonly: field.templateOptions.sasReadonly,
                required: field.templateOptions.validation?.validation.required ?? false,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || '',
                    pattern: this.sanitizePattern(field.templateOptions.validation?.validation.pattern) || '',
                    patternText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.patternText']) || this.cleanTranslate(field.templateOptions.validation?.validation.patternText) || ''
                },
            },
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'] ?? field.templateOptions.sasReadonly,
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapSwitch(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasSwitch',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                sasUseRemove: field.templateOptions.useRemove,
                columnPosition: field.templateOptions.columnPosition,
            },
            expressions: {
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapSubForm(field: any, fieldSchema:any):any {
        const mappedField: any = {
            key: field.key,
            type: 'selectSection',
            //id: field.id,
            //name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                btnText: this.cleanTranslate(field.templateOptions.btnText),
                defaultText: this.cleanTranslate(field.expressionProperties?.['templateOptions.defaultText']) || this.cleanTranslate(field.templateOptions.defaultText),
                defaultValue: null,
                multiple: field.templateOptions.multiple,
                options: field.templateOptions.options,
                fields: field.templateOptions.fields
            }
        };

        //if (fieldSchema.hideExpression?.length) {
        //    mappedField.expressions.hide = fieldSchema.hideExpression;
        //}

        return mappedField;
    }

    private mapRepeatSection(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'repeatSection',
            id: field.id,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                btnText: field.templateOptions.btnText ?? '',
                btnRemoveText: field.templateOptions.btnRemoveText ?? '',
                fields: field.templateOptions.fields,
                multiple: field.templateOptions.multiple,
                options: field.templateOptions.options,
                allowCollapse: field.templateOptions.allowCollapse,
                titleFields: field.templateOptions.titleFields,
                useCache: field.templateOptions.useCache
            },
            expressions: {
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapStreet(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasStreet',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) && this.cleanTranslate(field.templateOptions.placeholder),
                apiUrl: field.templateOptions.dataSource.url,
                sasOnlyName: field.templateOptions.sasOnlyName || false,
                maxLength: field.templateOptions.maxLength || 200,
                readonly: field.templateOptions.sasReadonly,
                required: field.templateOptions.validation?.validation.required ?? false,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || ''
                },
            },
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'] ?? field.templateOptions.sasReadonly,
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapGazetteerUprn(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasGazetteerUprn',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) || this.cleanTranslate(field.templateOptions.placeholder),
                maxLength: field.templateOptions.maxLength,
                readonly: field.templateOptions.sasReadonly,
                required: field.templateOptions.validation?.validation.required ?? false,
                maxPostcodes: field.templateOptions.maxPostcodes,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || '',
                    pattern: this.sanitizePattern(field.templateOptions.validation?.validation.pattern) || '',
                    patternText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.patternText']) || this.cleanTranslate(field.templateOptions.validation?.validation.patternText) || ''
                },
            },
            expressions: {
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapTimepicker(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            type: 'sasTimepicker',
            id: field.id,
            name: field.name,
            props: {
                label: this.cleanTranslate(field.expressionProperties?.['templateOptions.label']) || this.cleanTranslate(field.templateOptions.label),
                helptext: this.cleanTranslate(field.expressionProperties?.['templateOptions.helptext']) || this.cleanTranslate(field.templateOptions.helptext),
                placeholder: this.cleanTranslate(field.expressionProperties?.['templateOptions.placeholder']) && this.cleanTranslate(field.templateOptions.placeholder),
                readonly: field.templateOptions.sasReadonly,
                required: field.templateOptions.validation?.validation.required ?? false,
                columnPosition: field.templateOptions.columnPosition,
                validation: {
                    requiredText: this.cleanTranslate(field.expressionProperties?.['templateOptions.validation.validation.requiredText']) || this.cleanTranslate(field.templateOptions.validation?.validation.requiredText) || ''
                },
            },
            expressions: {
                'props.readonly': field.expressionProperties?.['templateOptions.sasReadonly'] ?? field.templateOptions.sasReadonly,
                'props.required': field.expressionProperties?.['templateOptions.validation.validation.required'] ?? false,
                'props.disabled': 'formState.disabled'
            }
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    private mapHtml(field: any, fieldSchema: any) {
        const mappedField: any = {
            key: field.key,
            template: field.template,
            expressions: {}
        };

        if (fieldSchema.hideExpression?.length) {
            mappedField.expressions.hide = fieldSchema.hideExpression;
        }

        return mappedField;
    }

    //#endregion

    private cleanTranslate(text: string) {
        return text?.replace(/["|]\s*\|\s*translate/g, '').replace(/"/g, '').replace(/^'(.*)'$/, '$1');
    }

    private sanitizePattern(pattern: string = '') {
        // Remove surrounding single quotes if present
        if (pattern.startsWith("'") && pattern.endsWith("'")) {
            pattern = pattern.slice(1, -1);
        }

        // Escape backslashes and dots for string-based regex
        return pattern.replace(/\\/g, '\\\\').replace(/\./g, '\\.');
    }

    /**
     * Updates the options for a specific field in the fields array and optionally removes the data source type.
     * @param fields Array of field objects to search within.
     * @param key The unique identifier of the field to update.
     * @param options Array of options to set for the field.
     * @param removeDataSourceType Optional flag to remove the data source type from the field (used for dropdowns that is using the config sheet).
     * @returns The updated fields array.
     */
    setupFieldOptions(fields: any[], key: string, options: any[], removeDataSourceType?: boolean): any[] {
        const roots = fields && fields.length > 0 && fields[0].fieldGroup
            ? fields.flatMap(f => f.fieldGroup)
            : fields;
        const field = this.findFieldById(roots, key);

        if (field){
            field.templateOptions.options = options;

            if (removeDataSourceType) {
                field.templateOptions.dataSource.type = '';
            }
        }

        return fields;
    }

    // Also searches the raw child fields of repeat/select sections (templateOptions.fields).
    private findFieldById(fields: any[], key: string): any {
        for (const f of fields ?? []) {
            if (f?.id === key) {
                return f;
            }

            const nested = f?.templateOptions?.fields;

            if (Array.isArray(nested)) {
                const found = this.findFieldById(nested, key);

                if (found) {
                    return found;
                }
            }
        }

        return undefined;
    }

    getFieldErrorMessage(field: any): string {
        if (field.formControl.errors?.['required'] && field.props['validation'].requiredText) {
            return this.translate.instant(field.props['validation'].requiredText);
        }
        if (field.formControl.errors?.['pattern'] && field.props['validation'].patternText) {
            return this.translate.instant(field.props['validation'].patternText);
        }
        return '';
    }
}
